/**
 * useServerPDF
 *
 * Reusable hook for downloading server-generated PDFs from the
 * /api/generate-pdf/:type/:id routes. Templates are stored in the
 * pdf_templates table and can be edited by admins without a frontend deploy.
 *
 * Usage:
 *   const { downloadPDF, isGenerating, error } = useServerPDF();
 *   await downloadPDF({ type: 'contract', id: contractId, filename: 'Contract_XYZ.pdf' });
 *
 * Supported types (matches backend routes): 'contract', 'amortization'.
 * Additional types can be added server-side without changing this hook.
 */

import { useState, useCallback } from 'react';
import { apiDownload } from '../utils/apiClient';

const useServerPDF = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const downloadPDF = useCallback(async ({ type, id, filename, body = {} }) => {
    if (!type || !id) {
      throw new Error('useServerPDF: type and id are required');
    }

    setIsGenerating(true);
    setError(null);

    try {
      const blob = await apiDownload(`/generate-pdf/${type}/${id}`, body);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${type}_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      return blob;
    } catch (err) {
      // Blob responses carry JSON error bodies — read them so the message is useful
      let message = err?.message || 'Failed to generate PDF';
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.error?.message || parsed?.message || message;
        } catch {
          // Fall through with default message
        }
      }
      setError(message);
      throw new Error(message);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { downloadPDF, isGenerating, error, clearError };
};

export default useServerPDF;
